/**
 * The `bar` visual adapter (SPEC §9.4).
 *
 * A column chart: the same category-x, value-y shape as `line`, drawn as bars.
 * Named by four seed reports (RFT's water-only chart, `login_activity`,
 * `report_registry`) and, unlike a line, its mark *is* the click target — so
 * the plot-band handler in `Chart.tsx` is generous rather than necessary here.
 *
 * Like every adapter it does no formatting of its own: axis labels and the
 * tooltip call into `lib/format.ts`, so a currency on an axis matches the same
 * currency in a table cell.
 */

import type { EChartsOption } from 'echarts'

import {
  axisLabel,
  axisLabelFormatter,
  axisLine,
  axisTooltipFormatter,
  baseOption,
  baseTooltip,
  seriesColor,
  splitLine,
} from '../../lib/charts'
import type { Cell, ColumnSpec, VisualSpec } from '../../lib/types'
import { categoryData, resolveSeriesColumns, resolveXColumn, seriesData } from './cartesian'

export function barOption(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  accent: string,
): EChartsOption {
  const xColumn = resolveXColumn(visual, columns)
  const categories = categoryData(visual, columns, rows)
  const seriesColumns = resolveSeriesColumns(visual, columns)

  const yColumn = seriesColumns[0]
  if (yColumn === undefined) {
    throw new Error(`visual ${visual.id} is a bar with no series`)
  }

  return {
    ...baseOption(),
    tooltip: {
      ...baseTooltip(),
      // A column's hit target is the bar, so the pointer is a shadow band over
      // the whole category rather than the crosshair a line uses.
      axisPointer: { type: 'shadow' },
      formatter: axisTooltipFormatter(xColumn, seriesColumns),
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { ...axisLabel(), formatter: axisLabelFormatter(xColumn) },
      axisLine: axisLine(),
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { ...axisLabel(), formatter: axisLabelFormatter(yColumn) },
      splitLine: splitLine(),
    },
    series: visual.series.map((series, index) => ({
      type: 'bar',
      name: seriesColumns[index]?.label ?? series.column,
      data: seriesData(columns, rows, series.column),
      itemStyle: { color: seriesColor(series, accent, index) },
      // A ceiling on width so a two-bar chart does not draw two half-page slabs.
      barMaxWidth: 40,
    })),
    // A long category axis needs the same zoom the line series gets.
    ...(rows.length > 60
      ? { dataZoom: [{ type: 'inside' as const }, { type: 'slider' as const, height: 18 }] }
      : {}),
  }
}
